import { IAction } from '@interface/IAction';
import { execSafeSaga } from '@lib/helpers';
import { getFreeCashBalanceSummarySaga } from '@services/funder/saga';
import { all, call, put, select, takeEvery, takeLeading, fork } from 'redux-saga/effects';
import { setPreSignedSellerReportUrl, actionType, setAvailableSubAssetList, setKeyFigure, setLiveReceivableList, setPurchaseResult, setReceivableDetail, setSellerLiveAsset, setSellerPreviousAsset, setSellerStats, setSubscribedAsset, getCreditEnhancementMapping, setCreditEnhancementMapping } from './action';
import { purchaseAssetWithChecking, getReceivableDetail, getReceivables, getReceivableSellerReportUrl, getSellerKeyFigure, getSellerStats, getSubAssetList, getSubscribedAssetList, purchaseAssets, getCreditEnhancementUrl } from './marketplace.service';
import { IDisplayName } from '@/interface/marketplace/DisplayName';
import { IPartner } from '@/interface/marketplace/Partners';

function* getSubscribedAssetTypeList() {
    yield execSafeSaga(function* () {
        const assetTypeList = yield call(getSubscribedAssetList);
        yield put(setSubscribedAsset(assetTypeList))
    });
}

function* getLiveReceivableList() {
    yield execSafeSaga(function* () {
        const liveAsset = yield call(getReceivables, ['LIVE']);
        yield put(setLiveReceivableList(liveAsset));
    });
}

function* getAvailableSubAssetListSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { assetId, } } = action;
        const subAssetList = yield call(getSubAssetList, assetId);
        yield all([
            put(setAvailableSubAssetList(assetId, subAssetList)),
            put({ type: actionType.SET_BUY_NOW_MODAL_INITIALIZED })
        ]);
    });
}

function* getReceivableDetailSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId } } = action;
        const detail = yield call(getReceivableDetail, receivableId);
        yield put(setReceivableDetail(detail));
    });
}

function* initReceivableDetailPage(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId } } = action;
        yield all([
            fork(getAvailableSubAssetListSaga, { type: '', payload: { assetId: receivableId } }),
            getReceivableDetailSaga(action)
        ])
        const detail = yield select(state => state.MarketplaceReducer.assetDetail);
        const { sellerCorporateId } = detail;
        yield call(getSellerKeyFigureSaga, { type: '', payload: { sellerCorporateId, receivableId } })
    });
}

function* getSellerStatistics(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId, sellerId, obligorId } } = action;
        const stats = yield call(getSellerStats, sellerId, obligorId);
        yield put(setSellerStats(receivableId, stats));
    });
}

function* getSellerKeyFigureSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { sellerCorporateId, receivableId } } = action;
        const keyFigure = yield call(getSellerKeyFigure, sellerCorporateId);
        yield put(setKeyFigure(receivableId, keyFigure));
    });
}

function* initReceivableSellerStatisticsPageSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId } } = action;
        let assetDetail = yield select((state) => state.MarketplaceReducer.assetDetail);
        if (!assetDetail || assetDetail._id !== receivableId) {
            yield call(getReceivableDetailSaga, action);
            assetDetail = yield select((state) => state.MarketplaceReducer.assetDetail);
        }

        const { sellerCorporateId, sellerId, obligorId } = assetDetail;

        yield all([
            getReceivableDetailSaga(action),
            getSellerKeyFigureSaga({ type: '', payload: { sellerCorporateId, receivableId } }),
            getSellerStatistics({ type: '', payload: { sellerId, obligorId, receivableId } })
        ]);
    });
}

function* getSellerLiveReceivables(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { sellerId } } = action;
        const asset = yield call(getReceivables, ['LIVE'], sellerId);
        yield put(setSellerLiveAsset(sellerId, asset));
    });
}

function* getSellerSoldReceivables(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { sellerId } } = action;
        const asset = yield call(getReceivables, ['CLOSED', 'ADVANCED', 'OVERDUE', 'REMITTED_AUCTION', 'REMITTED'], sellerId);
        yield put(setSellerPreviousAsset(sellerId, asset));
    });
}


function* initReceivableSellerTradeRecordsSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId } } = action;
        let assetDetail = yield select((state) => state.MarketplaceReducer.assetDetail);

        if (!assetDetail || assetDetail._id !== receivableId) {
            yield call(getReceivableDetailSaga, action);
            assetDetail = yield select((state) => state.MarketplaceReducer.assetDetail);
        }
        const { sellerId } = assetDetail;
        yield all([
            getReceivableDetailSaga(action),
            getSellerLiveReceivables({ type: '', payload: { sellerId } }),
            getSellerSoldReceivables({ type: '', payload: { sellerId } })
        ]);
    });
}

function* purchaseAssetSaga(action: IAction) {
    const { payload: { assetId, amount } } = action;
    //TODO: add cash advance logic
    const message = yield call(purchaseAssets, assetId, amount);
    yield all([
        getAvailableSubAssetListSaga({ type: '', payload: { assetId } }),
        getFreeCashBalanceSummarySaga(),
        put(setPurchaseResult({ message }))
    ]);
}

function* getPreSignedSellerReportUrlSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const { payload: { receivableId } } = action;
        const url = yield call(getReceivableSellerReportUrl, receivableId);
        yield put(setPreSignedSellerReportUrl(url));
    });
}

function* purchaseAssetWithSellerCheckingSaga(action: IAction) {
    const { payload: { assetId, amount } } = action;
    //TODO: add cash advance logic
    const result = yield call(purchaseAssetWithChecking, assetId, amount);
    if (result.message === 'OVER_SELLER_WARNING') {
        yield put(setPurchaseResult(result));
        return;
    }

    yield all([
        getAvailableSubAssetListSaga({ type: '', payload: { assetId } }),
        getFreeCashBalanceSummarySaga(),
        put(setPurchaseResult(result))
    ]);
}

function* getCreditEnhancementMappingSaga(action: IAction) {
    yield execSafeSaga(function* () {
        const result = yield call(getCreditEnhancementUrl);
        const creditMap: Map<string, IDisplayName> = new Map();
        result.forEach((item: IPartner) => creditMap.set(item.creditEnhancementCode, item.displayName)); 
        yield put(setCreditEnhancementMapping(creditMap));
    });
}

function* rootSaga() {
    yield all([
        takeEvery(actionType.GET_SUBSCRIBED_ASSET_TYPE, getSubscribedAssetTypeList),
        takeEvery(actionType.GET_LIVE_RECEIVABLE_LIST, getLiveReceivableList),
        takeEvery(actionType.GET_PURCHASE_SUB_ASSETS, getAvailableSubAssetListSaga),
        takeEvery(actionType.INIT_RECEIVABLE_DETAIL_PAGE, initReceivableDetailPage),
        takeEvery(actionType.INIT_RECEIVABLE_SELLER_STATISTICS_PAGE, initReceivableSellerStatisticsPageSaga),
        takeEvery(actionType.SET_PURCHASE_ASSETS, purchaseAssetSaga),
        takeEvery(actionType.GET_CREDIT_ENHANCEMENT_MAPPING, getCreditEnhancementMappingSaga),
        takeLeading(actionType.GET_PRE_SIGNED_SELLER_REPORT_URL, getPreSignedSellerReportUrlSaga),
        takeLeading(actionType.PURCHASE_SUB_ASSET_WITH_CHECKING, purchaseAssetWithSellerCheckingSaga)
    ]);
}

export default rootSaga;
